import type { ListOutput } from '@allocab/allocab-standard-shared-types';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import type {
  ExtranetIncidentOutput,
  ExtranetIncidentsListInput,
  IncidentsQueryInput,
} from '../../../types/extranet-api.types';
import { getExtranetApi } from '../extranetApi';
import { toPageableRangeInput } from '../mappers';
import { extranetQueryKeys } from '../queryKeys';

/**
 * `GET /v1/incidents` — depuis la v1.9.0 le back filtre lui-même par catégorie,
 * priorité et statut, et trie par date via `sortDirection`. Tout est donc envoyé
 * au serveur : plus de filtre ni de tri côté client, qui ne portaient que sur la
 * page courante.
 *
 * `ExtranetIncidentOutput` inclut ride/timeline/attachments inline — pas
 * d'endpoint de détail séparé, le drawer lit le cache de cette liste.
 */
export const useExtranetIncidentsList = (input: ExtranetIncidentsListInput) =>
  useQuery<ListOutput<ExtranetIncidentOutput>>({
    queryKey: extranetQueryKeys.incidents.list(input),
    queryFn: () =>
      getExtranetApi().get<IncidentsQueryInput, ListOutput<ExtranetIncidentOutput>>('/v1/incidents', {
        ...toPageableRangeInput(input, {
          offset: input.page && input.limit ? (input.page - 1) * input.limit : undefined,
          limit: input.limit,
        }),
        category: input.category?.length ? input.category : undefined,
        priority: input.priority?.length ? input.priority : undefined,
        status: input.status?.length ? input.status : undefined,
        sortDirection: input.sortDirection,
      }),
    enabled: Boolean(input.dateRange?.from && input.dateRange?.to),
    placeholderData: keepPreviousData,
  });
